import { AlertTriangle, ArrowLeft, CheckCircle2, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/ui/base/badge';
import { Card, CardContent } from '@/ui/base/card';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/ui/base/table';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';

import { useDominio } from './useDominio';

const FINANCEIRO = '/admin/parametros/financeiro';

/** Rótulo de cada regra de validação — mesmo texto do protótipo (`REGRA_LABEL`). */
const REGRA_LABEL: Record<string, string> = {
  DUPLICATA: 'Duplicata',
  VALIDADE_SEM_DIAS: 'Validade sem prazo',
  BACKFILL_INCOMPLETO: 'Backfill incompleto',
  REGRA_DIVERGENTE_ENTRE_CATEGORIAS: 'Regra divergente entre categorias',
  CERTIDAO_COMO_MENSAL: 'Certidão como mensal',
  GUIA_ENCARGO_VIGENTE: 'Guia de encargo como VIGENTE',
};

/** Quantas regras de validação a apuração sempre verifica — número fixo, como no protótipo. */
const TOTAL_DE_REGRAS_VERIFICADAS = 6;

/**
 * `/admin/parametros/financeiro/validacao-documentos` — validação do backfill
 * dos documentos de pagamento.
 *
 * Tela própria, e não `TelaDeDominio`, porque no protótipo ela também é
 * diferente: em vez de uma tabela de cadastro, mostra o RESULTADO de uma
 * apuração — uma linha por achado de regra violada (severidade, regra,
 * categoria, documento, periodicidade, mês de competência e detalhe) — e um
 * estado final de sucesso quando não há achado nenhum.
 *
 * NÃO TEM O BOTÃO "REEXECUTAR" do protótipo: reexecutar a validação depende de
 * um serviço que ainda não existe, e o item 7 das regras de interface do
 * CLAUDE.md proíbe renderizar o que o usuário não pode fazer.
 *
 * Os achados vêm do dado simulado, como o resto desta fase; nenhuma contagem é
 * arredondada para dar a impressão de que o backfill passou quando não passou.
 */
export function ValidacaoDeDocumentos() {
  const { estado, recarregar } = useDominio('validacaoDocumentos');

  if (estado.situacao === 'carregando') {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-3 py-16">
          <Spinner />
          <p className="text-sm text-muted-foreground">Carregando a validação…</p>
        </CardContent>
      </Card>
    );
  }

  if (estado.situacao === 'erro') {
    return (
      <ErrorState
        variant="load-failed"
        title="Não foi possível carregar"
        primaryAction={{ label: 'Tentar de novo', onClick: recarregar }}
        secondaryAction={{ label: 'Financeiro e tributário', to: FINANCEIRO, variant: 'outline' }}
        technicalDetails={estado.mensagem}
      />
    );
  }

  // `useDominio` marca lista vazia como "vazio" antes de chegar em "pronto" — os
  // dois casos significam a MESMA coisa aqui: nenhum achado, backfill validado.
  const achados = estado.situacao === 'pronto' ? estado.linhas : [];
  const erros = achados.filter((a) => a.severidade === 'ERRO').length;
  const avisos = achados.filter((a) => a.severidade === 'AVISO').length;
  const regrasComAchado = new Set(achados.map((a) => String(a.regra))).size;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={FINANCEIRO}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Financeiro e tributário
        </Link>
      </div>

      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">
          Validação do backfill — Documentos de pagamento
        </h1>
        <p className="text-sm text-muted-foreground">
          Confirma se periodicidade e mês de competência foram preenchidos corretamente para pessoa
          física, cooperativa e pessoa jurídica.
        </p>
      </header>

      {achados.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <CheckCircle2 className="h-8 w-8 text-success" aria-hidden="true" />
            <p className="font-semibold">Backfill validado</p>
            <p className="max-w-md text-sm text-muted-foreground">
              Nenhuma divergência encontrada em periodicidade, mês de competência, duplicatas ou
              consistência entre categorias.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <dl className="grid gap-4 sm:grid-cols-3">
            <Indicador
              icone={ShieldAlert}
              rotulo="Erros"
              valor={erros}
              detalhe="Duplicatas e campos vazios — corrigir antes de usar em produção."
            />
            <Indicador
              icone={AlertTriangle}
              rotulo="Avisos"
              valor={avisos}
              detalhe="Divergências entre categorias — validar com a área financeira."
            />
            <Indicador
              icone={CheckCircle2}
              rotulo="Regras verificadas"
              valor={TOTAL_DE_REGRAS_VERIFICADAS}
              detalhe={`${regrasComAchado} regra(s) com achados nesta execução.`}
            />
          </dl>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableCaption className="sr-only">
                    Achados da validação do backfill, um por linha.
                  </TableCaption>
                  <TableHeader>
                    <TableRow>
                      <TableHead scope="col">Severidade</TableHead>
                      <TableHead scope="col">Regra</TableHead>
                      <TableHead scope="col">Categoria</TableHead>
                      <TableHead scope="col">#</TableHead>
                      <TableHead scope="col">Documento</TableHead>
                      <TableHead scope="col">Periodicidade</TableHead>
                      <TableHead scope="col">Mês de competência</TableHead>
                      <TableHead scope="col">Detalhe</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {achados.map((achado, indice) => {
                      const regra = String(achado.regra ?? '');
                      return (
                        <TableRow key={String(achado.id ?? indice)}>
                          <TableCell>
                            <Badge
                              variant={achado.severidade === 'ERRO' ? 'destructive' : 'outline'}
                            >
                              {String(achado.severidade ?? '')}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">
                            {REGRA_LABEL[regra] ?? regra}
                          </TableCell>
                          <TableCell>{String(achado.categoria ?? '—')}</TableCell>
                          <TableCell>{String(achado.ordem ?? '—')}</TableCell>
                          <TableCell>{String(achado.nome ?? '—')}</TableCell>
                          <TableCell>{String(achado.periodicidade ?? '—')}</TableCell>
                          <TableCell>{String(achado.mesCompetenciaRegra ?? '—')}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {String(achado.detalhe ?? '—')}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Indicador({
  icone: Icone,
  rotulo,
  valor,
  detalhe,
}: {
  icone: typeof CheckCircle2;
  rotulo: string;
  valor: number;
  detalhe: string;
}) {
  return (
    <Card>
      <CardContent className="space-y-1 py-4">
        <dt className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icone className="h-4 w-4" aria-hidden="true" />
          {rotulo}
        </dt>
        <dd className="text-2xl font-semibold tabular-nums">{valor}</dd>
        <p className="text-xs text-muted-foreground">{detalhe}</p>
      </CardContent>
    </Card>
  );
}

export default ValidacaoDeDocumentos;
